import { useState } from "react";
import { NavLink, useNavigate } from "react-router";
import type { Project } from "../api/types";
import { useT } from "../i18n";
import { colorHex } from "../lib/colors";
import {
  activeCount,
  dueDay,
  isLive,
  labelCount,
  type ProjectNode,
  personalProjects,
  projectTree,
  sortedLabels,
} from "../lib/tasks";
import { useApp, useSyncState } from "../store";
import { updateProject } from "../sync/actions";
import {
  IconChevronDown,
  IconChevronRight,
  IconCompleted,
  IconDrop,
  IconFilters,
  IconHash,
  IconInbox,
  IconLabel,
  IconMore,
  IconPlus,
  IconSearch,
  IconSidebar,
  IconToday,
  IconUpcoming,
} from "../ui/icons";
import { usePrefs } from "../ui/prefs";
import { Avatar, useAnchor } from "../ui/primitives";
import { ProjectDialog, ProjectMenu } from "../views/ProjectMenu";
import { UserMenu } from "./UserMenu";

function useCloseOnMobile() {
  return () => {
    if (window.innerWidth < 750) useApp.getState().set({ sidebarOpen: false });
  };
}

function NavItem({
  to,
  icon,
  label,
  count,
  testId,
  extra,
  onContextMenu,
  style,
}: {
  to: string;
  icon: React.ReactNode;
  label: React.ReactNode;
  count?: number;
  testId?: string;
  extra?: React.ReactNode;
  onContextMenu?: (e: React.MouseEvent) => void;
  style?: React.CSSProperties;
}) {
  const closeMobile = useCloseOnMobile();
  return (
    <NavLink
      to={to}
      className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
      data-testid={testId}
      onClick={closeMobile}
      onContextMenu={onContextMenu}
      style={style}
    >
      <span className="nav-icon">{icon}</span>
      <span className="label">{label}</span>
      {count ? <span className={`count${extra ? " hide-on-hover" : ""}`}>{count}</span> : null}
      {extra}
    </NavLink>
  );
}

function ProjectItem({ node }: { node: ProjectNode }) {
  const state = useSyncState();
  const t = useT();
  const menu = useAnchor<Element>();
  const p = node.project;
  const hasChildren = node.children.length > 0;
  const menuButton = (
    <span className={`hover-only${menu.anchor ? " open" : ""}`}>
      <button
        type="button"
        className="icon-btn"
        style={{ width: 24, height: 24 }}
        aria-label={t("more")}
        data-testid="project-more"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          menu.open(e);
        }}
      >
        <IconMore size={18} />
      </button>
    </span>
  );
  return (
    <>
      <div style={{ position: "relative" }}>
        <NavItem
          to={`/app/project/${p.id}`}
          testId="sidebar-project"
          style={{ paddingLeft: 6 + node.depth * 18 }}
          icon={
            <span style={{ color: colorHex(p.color), display: "inline-flex" }}>
              <IconHash size={20} />
            </span>
          }
          label={p.name}
          count={activeCount(state, p.id)}
          extra={menuButton}
          onContextMenu={(e) => {
            e.preventDefault();
            menu.openAt(e.currentTarget);
          }}
        />
        {hasChildren && (
          <button
            type="button"
            className="tree-toggle"
            style={{ position: "absolute", left: -14 + node.depth * 18, top: 7 }}
            aria-label={p.is_collapsed ? t("expand") : t("collapse")}
            onClick={() => void updateProject(p.id, { is_collapsed: !p.is_collapsed })}
          >
            {p.is_collapsed ? <IconChevronRight size={14} /> : <IconChevronDown size={14} />}
          </button>
        )}
      </div>
      <ProjectMenu project={p} anchor={menu.anchor} onClose={menu.close} />
      {hasChildren && !p.is_collapsed && node.children.map((c) => <ProjectItem key={c.project.id} node={c} />)}
    </>
  );
}

function Group({
  title,
  children,
  actions,
  testId,
  defaultOpen = true,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  testId?: string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={testId}>
      <div className="nav-section-title">
        <button type="button" style={{ flex: 1, textAlign: "left" }} onClick={() => setOpen(!open)}>
          {title}
        </button>
        <span className="actions">
          {actions}
          <button type="button" className="tree-toggle" onClick={() => setOpen(!open)} aria-label="toggle">
            {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          </button>
        </span>
      </div>
      {open && children}
    </div>
  );
}

export function Sidebar() {
  const state = useSyncState();
  const prefs = usePrefs();
  const t = useT();
  const navigate = useNavigate();
  const open = useApp((s) => s.sidebarOpen);
  const set = useApp((s) => s.set);
  const [addProject, setAddProject] = useState(false);
  const user = state.user;
  const userA = useAnchor();

  const inbox = Object.values(state.projects).find((p) => p.inbox_project);
  let todayCount = 0;
  for (const it of Object.values(state.items)) {
    if (!isLive(state, it)) continue;
    const d = dueDay(it, prefs.tz);
    if (d && d <= prefs.today) todayCount++;
  }
  const tree = projectTree(personalProjects(state));
  const favorites: { key: string; node: React.ReactNode }[] = [
    ...Object.values(state.projects)
      .filter((p) => p.is_favorite)
      .map((p: Project) => ({
        key: `p${p.id}`,
        node: (
          <NavItem
            key={`fp${p.id}`}
            to={p.inbox_project ? "/app/inbox" : `/app/project/${p.id}`}
            icon={
              <span style={{ color: colorHex(p.color), display: "inline-flex" }}>
                <IconHash size={20} />
              </span>
            }
            label={p.name}
            count={activeCount(state, p.id)}
          />
        ),
      })),
    ...sortedLabels(state)
      .filter((l) => l.is_favorite)
      .map((l) => ({
        key: `l${l.id}`,
        node: (
          <NavItem
            key={`fl${l.id}`}
            to={`/app/label/${encodeURIComponent(l.name)}`}
            icon={
              <span style={{ color: colorHex(l.color), display: "inline-flex" }}>
                <IconLabel size={20} />
              </span>
            }
            label={l.name}
            count={labelCount(state, l.name)}
          />
        ),
      })),
    ...Object.values(state.filters)
      .filter((f) => f.is_favorite)
      .map((f) => ({
        key: `f${f.id}`,
        node: (
          <NavItem
            key={`ff${f.id}`}
            to={`/app/filter/${f.id}`}
            icon={
              <span style={{ color: colorHex(f.color), display: "inline-flex" }}>
                <IconDrop size={20} />
              </span>
            }
            label={f.name}
          />
        ),
      })),
  ];
  const workspaces = Object.values(state.workspaces);

  return (
    <>
      {open && (
        <div
          className="backdrop mobile-only"
          onClick={() => set({ sidebarOpen: false })}
          onKeyDown={() => undefined}
          role="presentation"
        />
      )}
      <nav className={`sidebar${open ? "" : " closed"}`} aria-label="sidebar" data-testid="sidebar">
        <div className="sidebar-top">
          <button type="button" className="user-btn" onClick={userA.open} data-testid="user-menu">
            <Avatar name={user?.full_name || user?.email || "?"} size={26} />
            <span className="name">{user?.full_name || user?.email}</span>
            <IconChevronDown size={14} />
          </button>
          <button
            type="button"
            className="icon-btn"
            onClick={() => set({ sidebarOpen: false })}
            aria-label={t("closeSidebar")}
            data-testid="sidebar-close"
          >
            <IconSidebar size={22} />
          </button>
        </div>
        <div className="sidebar-scroll">
          <button
            type="button"
            className="nav-item nav-add-task"
            onClick={() => set({ quickAdd: { open: true } })}
            data-testid="sidebar-add-task"
          >
            <span className="nav-icon">
              <span className="plus-circle">
                <IconPlus size={16} />
              </span>
            </span>
            <span className="label">{t("addTask")}</span>
          </button>
          <button
            type="button"
            className="nav-item"
            onClick={() => set({ searchOpen: true })}
            data-testid="sidebar-search"
          >
            <span className="nav-icon">
              <IconSearch size={22} />
            </span>
            <span className="label">{t("search")}</span>
          </button>
          <NavItem
            to="/app/inbox"
            testId="nav-inbox"
            icon={<IconInbox size={22} />}
            label={t("inbox")}
            count={inbox ? activeCount(state, inbox.id) : 0}
          />
          <NavItem
            to="/app/today"
            testId="nav-today"
            icon={<IconToday size={22} day={Number(prefs.today.slice(8))} />}
            label={t("today")}
            count={todayCount}
          />
          <NavItem to="/app/upcoming" testId="nav-upcoming" icon={<IconUpcoming size={22} />} label={t("upcoming")} />
          <NavItem
            to="/app/filters-labels"
            testId="nav-filters"
            icon={<IconFilters size={22} />}
            label={t("filtersLabels")}
          />
          <NavItem
            to="/app/completed"
            testId="nav-completed"
            icon={<IconCompleted size={22} />}
            label={t("completed")}
          />

          {favorites.length > 0 && (
            <Group title={t("favorites")} testId="favorites">
              {favorites.map((f) => (
                <div key={f.key}>{f.node}</div>
              ))}
            </Group>
          )}

          <Group
            title={t("myProjects")}
            testId="my-projects"
            actions={
              <button
                type="button"
                className="tree-toggle"
                onClick={() => setAddProject(true)}
                aria-label={t("addProject")}
                data-testid="add-project"
              >
                <IconPlus size={16} />
              </button>
            }
          >
            {tree.map((n) => (
              <ProjectItem key={n.project.id} node={n} />
            ))}
          </Group>

          {workspaces.map((w) => (
            <Group key={w.id} title={w.name} testId="workspace">
              {projectTree(Object.values(state.projects).filter((p) => p.workspace_id === w.id)).map((n) => (
                <ProjectItem key={n.project.id} node={n} />
              ))}
            </Group>
          ))}
        </div>
      </nav>
      {addProject && <ProjectDialog onClose={() => setAddProject(false)} />}
      <UserMenu anchor={userA.anchor} onClose={userA.close} onNavigate={navigate} />
    </>
  );
}
